import type {
  Status,
  TrackingLinkAccessType,
  TrackingLinkStatus,
} from "../types";
import type { Tone } from "../../../../../../components/ui";

const STATUS_TONE_BY_ID = {
  R5tW0uX4yY9cF6dH2aI8: "primary",
  W0yB5zC9dD4hK1iM7fN3: "error",
  S6uX1vY5zZ0dG7eI3bJ9: "neutral",
} as const satisfies Record<string, Tone>;

const STATUS_TONE_BY_NAME = {
  active: "primary",
  expired: "error",
  pending: "neutral",
  inactive: "neutral",
} as const satisfies Record<string, Tone>;

const TONE_BY_ID: Record<string, Tone> = STATUS_TONE_BY_ID;
const TONE_BY_NAME: Record<string, Tone> = STATUS_TONE_BY_NAME;

export type ResolvedStatus = TrackingLinkStatus | "unknown";

const STATUS_NAME_BY_ID: Record<string, TrackingLinkStatus> = {
  R5tW0uX4yY9cF6dH2aI8: "active",
  W0yB5zC9dD4hK1iM7fN3: "expired",
  S6uX1vY5zZ0dG7eI3bJ9: "inactive",
};

const STATUS_NAMES: TrackingLinkStatus[] = [
  "active",
  "inactive",
  "expired",
  "pending",
];

export const resolveStatus = (status: Status | null): ResolvedStatus => {
  if (!status) return "unknown";
  const byId = STATUS_NAME_BY_ID[status.id];
  if (byId) return byId;
  const name = status.name?.toLowerCase() as TrackingLinkStatus;
  return STATUS_NAMES.includes(name) ? name : "unknown";
};

export const getStatusTone = (status: Status | null): Tone => {
  if (!status) return "warning";
  return (
    TONE_BY_ID[status.id] ?? TONE_BY_NAME[status.name?.toLowerCase() ?? ""] ?? "warning"
  );
};

export const getStatusStyle = (
  status: Status | null,
): { backgroundColor: string; color: string } | undefined => {
  if (!status?.color) return undefined;
  if (TONE_BY_ID[status.id]) return undefined;
  return { backgroundColor: `${status.color}20`, color: status.color };
};

export const getStatusText = (status: Status | null): string => {
  if (!status) return "Desconocido";
  return status.name;
};


export const getAccessTypeKey = (
  accessType: TrackingLinkAccessType,
): string => {
  switch (accessType) {
    case "public":
      return "trackingLinks.filters.public";
    case "protected":
      return "trackingLinks.filters.protected";
    default:
      return accessType;
  }
};

export const getAccessTypeIcon = (
  accessType: TrackingLinkAccessType,
): string => {
  switch (accessType) {
    case "public":
      return "ri-global-line";
    case "protected":
      return "ri-lock-line";
    default:
      return "ri-link";
  }
};

export const isExpiringSoon = (expirationDate: any): boolean => {
  if (!expirationDate) return false;

  const expDate = expirationDate._seconds
    ? new Date(expirationDate._seconds * 1000)
    : new Date(expirationDate);

  const now = new Date();
  const hoursUntilExpiration =
    (expDate.getTime() - now.getTime()) / (1000 * 60 * 60);

  return hoursUntilExpiration > 0 && hoursUntilExpiration <= 24;
};

export const getExpirationText = (
  expirationDate: any,
  status: ResolvedStatus,
): { text: string; isWarning: boolean } => {
  if (!expirationDate) {
    return { text: "No definida", isWarning: false };
  }

  const expDate = expirationDate._seconds
    ? new Date(expirationDate._seconds * 1000)
    : new Date(expirationDate);

  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();

  if (status === "expired" || diffMs < 0) {
    const absDiffMs = Math.abs(diffMs);
    const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
    if (days > 0) {
      return { text: `${days} día${days > 1 ? "s" : ""}`, isWarning: false };
    }
    const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
    return { text: `${hours} hora${hours > 1 ? "s" : ""}`, isWarning: false };
  }

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return {
      text: `${days} día${days > 1 ? "s" : ""} ${hours}h`,
      isWarning: days === 0 && hours <= 4,
    };
  }

  return {
    text: `${hours} hora${hours > 1 ? "s" : ""}`,
    isWarning: hours <= 4,
  };
};

export const getRemainingTime = (
  expirationDate: any,
  status: ResolvedStatus,
): { text: string; isWarning: boolean } => {
  if (!expirationDate) {
    return { text: "--:--:--", isWarning: false };
  }

  const expDate = expirationDate._seconds
    ? new Date(expirationDate._seconds * 1000)
    : new Date(expirationDate);

  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();

  if (status === "expired" || diffMs < 0) {
    return { text: "Expirado", isWarning: false };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  const isWarning = totalSeconds <= 24 * 60 * 60; // Less than 24 hours

  return { text: formattedTime, isWarning };
};

export const getRemainingTimeWithDate = (
  expirationDate: any,
  status: ResolvedStatus,
  currentTime: Date = new Date(),
): { text: string; isWarning: boolean } => {
  if (!expirationDate) {
    return { text: "--:--:--", isWarning: false };
  }

  const expDate = expirationDate._seconds
    ? new Date(expirationDate._seconds * 1000)
    : new Date(expirationDate);

  const diffMs = expDate.getTime() - currentTime.getTime();

  if (status === "expired" || diffMs < 0) {
    return { text: "Expirado", isWarning: false };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  const isWarning = totalSeconds <= 24 * 60 * 60; // Less than 24 hours

  return { text: formattedTime, isWarning };
};
